import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import moment from "moment";

/** COMPONENT */
import { Box, IconButton, MenuItem, Tooltip, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckIcon from "@mui/icons-material/Check";
import SubjectIcon from "@mui/icons-material/Subject";
import HeaderCard from "../../component/cardCustom/headerCard";
import TableCustom from "../../component/table/tableCustom";
import TableRowCommon from "../../component/table/TableRowCommon";
import InputTextField from "../../component/input/inputTextField";
import InputDatePicker from "../../component/input/inputDatePicker";
import FilterSelect from "../../component/select/filterSelect";
import ButtonCustom from "../../component/button/buttonCustom";
import Loading from "../../component/loading/loading";
import { notiError } from "../../component/notification/notifications";

/** MODAL */
import ModalInheritanceDetail from "./modal/ModalInheritanceDetail";
import ModalInheritanceReview from "./modal/ModalInheritanceReview";
import ModalInheritanceLog from "./modal/ModalInheritanceLog";
import ModalInheritanceCreate from "./modal/ModalInheritanceCreate";

/** API */
import InheritanceTransferApi, {
  InheritanceStatus,
  InheritanceSummary,
  InheritanceTransferRow,
} from "../../api/inheritance-transfer/inheritance-transfer.api";

/** UTILS */
import { colors } from "../../constants/colors";
import {
  INHERITANCE_STATUS,
  INHERITANCE_STATUS_ALL,
  INHERITANCE_STATUS_OPTIONS,
  InheritanceCreatePrefill,
  InheritanceTransferLocationState,
  inheritanceStatusBg,
  inheritanceStatusColor,
  inheritanceStatusLabel,
} from "../../constants/inheritanceTransfer";
import { dateTimeToView } from "../../utils/date.utils";
import { getApiErrorMessage } from "../../utils/api-error.utils";
import { useMenuPermission } from "./useMenuPermission";

/** STYLE */
import * as Style from "./inheritanceTransfer.style";

const EMPTY_SUMMARY: InheritanceSummary = {
  all: 0,
  WAITING: 0,
  APPROVE: 0,
  NOT_APPROVE: 0,
};

export default function InheritanceTransfer() {
  const permissions = useMenuPermission();
  const location = useLocation<InheritanceTransferLocationState | undefined>();
  const history = useHistory();

  const [data, setData] = useState<InheritanceTransferRow[]>([]);
  const [summary, setSummary] = useState<InheritanceSummary>(EMPTY_SUMMARY);
  const [rowCount, setRowCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [page, setPage] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(10);
  /** ค่าที่พิมพ์อยู่ในช่อง — แยกจาก search เพื่อ debounce ไม่ให้ยิง API ทุก keystroke */
  const [searchInput, setSearchInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [status, setStatus] = useState<string>(INHERITANCE_STATUS_ALL);
  const [dateStart, setDateStart] = useState<string>("");
  const [dateEnd, setDateEnd] = useState<string>("");

  /** modal — ใช้ standalone mode ทุกตัว จึงต้อง conditional render จากตรงนี้ */
  const [openCreate, setOpenCreate] = useState<boolean>(false);
  /** ค่าเริ่มต้นของ modal สร้างคำร้อง เมื่อถูกพามาจากหน้าจัดการข้อมูลลูกค้า (หลังรับเอกสารผู้รับมรดก) */
  const [createPrefill, setCreatePrefill] = useState<InheritanceCreatePrefill | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [reviewRow, setReviewRow] = useState<InheritanceTransferRow | null>(null);
  const [logRow, setLogRow] = useState<InheritanceTransferRow | null>(null);

  /**
   * เข้ามาพร้อม createPrefill -> เปิด modal สร้างคำร้องให้ทันที
   * แล้ว replace ล้าง state ออกจาก history เลย ไม่งั้น reload / back-forward จะเด้ง modal ซ้ำ
   */
  useEffect(() => {
    const prefill = location.state?.createPrefill;
    if (!prefill) return;
    history.replace(location.pathname);
    if (!permissions.isCreate) return;
    setCreatePrefill(prefill);
    setOpenCreate(true);
    // อ่านครั้งเดียวตอนเข้าหน้า
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeCreate = () => {
    setOpenCreate(false);
    setCreatePrefill(null);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  /** ตัวกรองที่ใช้ร่วมกันระหว่างตารางกับ summary — summary ไม่รับ status เพื่อให้เห็นตัวเลขครบทุกสถานะ */
  const buildFilter = useCallback(() => {
    let condition: any = {};
    if (search) condition = { ...condition, keyword: search };
    if (dateStart) condition = { ...condition, date_from: dateStart };
    if (dateEnd) condition = { ...condition, date_to: dateEnd };
    return condition;
  }, [search, dateStart, dateEnd]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      let condition: any = { ...buildFilter(), page, limit: pageLimit };
      if (status !== INHERITANCE_STATUS_ALL) {
        condition = { ...condition, status: status as InheritanceStatus };
      }
      const res = await InheritanceTransferApi.findAll(condition);
      // envelope ของ endpoint ชุดนี้ซ้อน data สองชั้น ไม่ใช่ res.data ตรงๆ แบบ API รุ่นเก่า
      const paginated = res.data?.data;
      setData(paginated?.items || []);
      setRowCount(paginated?.meta?.totalItems || 0);
    } catch (error) {
      setData([]);
      setRowCount(0);
      notiError(getApiErrorMessage(error, "ดึงรายการคำร้องโอนมรดกไม่สำเร็จ"));
    } finally {
      setIsLoading(false);
    }
  }, [buildFilter, page, pageLimit, status]);

  const loadSummary = useCallback(async () => {
    try {
      const res = await InheritanceTransferApi.summary(buildFilter());
      setSummary(res.data?.data || EMPTY_SUMMARY);
    } catch (error) {
      // ตัวเลขข้าง dropdown ไม่ใช่ข้อมูลหลัก พลาดแล้วให้เงียบ ไม่รบกวนผู้ใช้ซ้ำกับ error ของตาราง
      setSummary(EMPTY_SUMMARY);
    }
  }, [buildFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  /** เรียกหลังสร้าง/พิจารณาสำเร็จ ให้ทั้งตารางและตัวเลขสถานะตรงกัน */
  const reload = () => {
    loadData();
    loadSummary();
  };

  const handleChangeDateStart = (value: any) => {
    setDateStart(value ? moment(value).format("YYYY-MM-DD") : "");
    setPage(1);
  };

  const handleChangeDateEnd = (value: any) => {
    setDateEnd(value ? moment(value).format("YYYY-MM-DD") : "");
    setPage(1);
  };

  const statusCount = (value: string) =>
    value === INHERITANCE_STATUS_ALL
      ? summary.all
      : summary[value as keyof InheritanceSummary] ?? 0;

  const headCells = [
    { id: "no", disablePadding: false, label: "NO", align: "center" },
    { id: "inheritanceNo", disablePadding: false, label: "เลขที่คำร้อง", align: "center" },
    { id: "owner", disablePadding: false, label: "ผู้ถือสิทธิเดิม", align: "center" },
    { id: "heir", disablePadding: false, label: "ผู้รับโอน", align: "center" },
    { id: "requestDate", disablePadding: false, label: "วันเวลาที่เพิ่มคำร้อง", align: "center" },
    { id: "reviewedDate", disablePadding: false, label: "วันเวลาที่พิจารณา", align: "center" },
    { id: "reviewer", disablePadding: false, label: "ผู้พิจารณา", align: "center" },
    { id: "status", disablePadding: false, label: "สถานะคำร้อง", align: "center" },
    { id: "action", disablePadding: false, label: "Action", align: "center" },
  ];

  const renderAction = (objData: InheritanceTransferRow) => {
    const isWaiting = objData.status === INHERITANCE_STATUS.WAITING;
    const viewDisabled = !permissions.isView;
    // พิจารณาได้เฉพาะคำร้องที่ยังรอ — แถวอื่นให้ปุ่มจางและกดไม่ได้ตามดีไซน์
    const reviewDisabled = !isWaiting || !permissions.isApporve;
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Tooltip title="รายละเอียดคำร้องโอนมรดก">
          <span>
            <IconButton
              disabled={viewDisabled}
              onClick={() => setDetailId(objData.id_inheritance_transfer)}
              sx={{ color: viewDisabled ? colors.disabledGray : colors.textLightGray }}
            >
              <DescriptionOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={isWaiting ? "พิจารณาคำร้อง" : "คำร้องนี้ถูกพิจารณาแล้ว"}>
          <span>
            <IconButton
              disabled={reviewDisabled}
              onClick={() => setReviewRow(objData)}
              sx={{ color: reviewDisabled ? colors.disabledGray : colors.textLightGray }}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="ประวัติการพิจารณา">
          <span>
            <IconButton
              disabled={viewDisabled}
              onClick={() => setLogRow(objData)}
              sx={{ color: viewDisabled ? colors.disabledGray : colors.textLightGray }}
            >
              <SubjectIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const renderData = (objData: InheritanceTransferRow, no: number) => {
    const runningNo = page * pageLimit - pageLimit + no + 1;
    const {
      id_inheritance_transfer,
      inheritance_no,
      owner_fullname,
      heir_fullname,
      request_date,
      reviewed_date,
      reviewer_name,
      status: rowStatus,
    } = objData;

    const objRenderData = {
      key: String(id_inheritance_transfer),
      id: String(id_inheritance_transfer),
      obj: objData,
      columns: [
        { option: "TEXT", align: "center", label: runningNo },
        {
          option: "COMPONENT",
          align: "center",
          component: (
            <Style.LinkText onClick={() => setDetailId(id_inheritance_transfer)}>
              {inheritance_no || "-"}
            </Style.LinkText>
          ),
        },
        { option: "TEXT", align: "center", label: owner_fullname || "-" },
        { option: "TEXT", align: "center", label: heir_fullname || "-" },
        // dateTimeToView(undefined) format วันนี้เงียบๆ ต้อง guard ก่อนเสมอ
        { option: "TEXT", align: "center", label: request_date ? dateTimeToView(request_date) : "–" },
        { option: "TEXT", align: "center", label: reviewed_date ? dateTimeToView(reviewed_date) : "–" },
        { option: "TEXT", align: "center", label: reviewer_name || "–" },
        {
          option: "COMPONENT",
          align: "center",
          component: (
            <Style.StatusChip
              chipColor={inheritanceStatusColor(rowStatus)}
              chipBg={inheritanceStatusBg(rowStatus)}
            >
              {inheritanceStatusLabel(rowStatus)}
            </Style.StatusChip>
          ),
        },
        { option: "COMPONENT", align: "center", component: renderAction(objData) },
      ],
    };

    return <TableRowCommon {...objRenderData} />;
  };

  return (
    <>
      {isLoading && <Loading show type="softLoading" />}
      <HeaderCard text="โอนมรดก" />
      <Style.MainContainer>
        <Row className="align-items-end">
          <Col lg={3} md={6} xs={12} className="my-1">
            <InputTextField
              value={searchInput}
              placeholder="ค้นหาเลขที่คำร้อง / ผู้ถือสิทธิเดิม / ผู้รับโอน"
              onchange={(e: any) => setSearchInput(e.target.value)}
            />
          </Col>
          <Col lg={2} md={6} xs={12} className="my-1">
            <FilterSelect
              labelId="inheritance-status"
              selectId="inheritance-status"
              value={status}
              onchange={(event: any) => {
                setStatus(event.target.value);
                setPage(1);
              }}
              renderValue={() => `สถานะ : ${inheritanceStatusLabel(status)}`}
              options={INHERITANCE_STATUS_OPTIONS.map((option: string) => (
                <MenuItem key={option} value={option}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: inheritanceStatusColor(option),
                        }}
                      />
                      {inheritanceStatusLabel(option)}
                    </Box>
                    <Typography component="span" sx={{ fontSize: 14, color: colors.textLightGray }}>
                      {statusCount(option)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            />
          </Col>
          <Col lg={2} md={6} xs={12} className="my-1">
            <InputDatePicker
              value={dateStart || null}
              onChange={handleChangeDateStart}
              onClear={() => handleChangeDateStart(null)}
              allowClear
              label="วันที่เริ่มต้น"
              placeholder="เลือกวันที่"
              disableEndDate={dateEnd || undefined}
            />
          </Col>
          <Col lg={2} md={6} xs={12} className="my-1">
            <InputDatePicker
              value={dateEnd || null}
              onChange={handleChangeDateEnd}
              onClear={() => handleChangeDateEnd(null)}
              allowClear
              label="วันที่สิ้นสุด"
              placeholder="เลือกวันที่"
              disableStartDate={dateStart || undefined}
            />
          </Col>
          <Col lg={3} md={6} xs={12} className="my-1">
            <ButtonCustom
              mode="add"
              style={{ width: "100%" }}
              textButton="โอนมรดก"
              disabled={!permissions.isCreate}
              onClick={() => setOpenCreate(true)}
            />
          </Col>
        </Row>

        <Style.TableContainer>
          <TableCustom
            headCells={headCells}
            customScroll
            page={page}
            pageLimit={pageLimit}
            sortType="ASC"
            sortBy=""
            rowCount={rowCount}
            rowsData={data.map((row, index) => renderData(row, index))}
            // API ชุดนี้ยังไม่รองรับการเรียงลำดับ หัวตารางจึงไม่มีปุ่ม sort
            onSort={() => { }}
            setPageLimit={(value: number) => {
              setPageLimit(value);
              setPage(1);
            }}
            setPage={(value: number) => setPage(value)}
            textEmptyData="ไม่พบคำร้องโอนมรดก"
            tableMinWidth={1400}
          />
        </Style.TableContainer>
      </Style.MainContainer>

      {openCreate && (
        <ModalInheritanceCreate
          prefill={createPrefill ?? undefined}
          onClose={closeCreate}
          onSuccess={() => {
            closeCreate();
            setPage(1);
            reload();
          }}
        />
      )}

      {detailId !== null && (
        <ModalInheritanceDetail id={detailId} onClose={() => setDetailId(null)} />
      )}

      {reviewRow && (
        <ModalInheritanceReview
          id={reviewRow.id_inheritance_transfer}
          inheritanceNo={reviewRow.inheritance_no}
          ownerName={reviewRow.owner_fullname}
          heirName={reviewRow.heir_fullname}
          onClose={() => setReviewRow(null)}
          onSuccess={() => {
            setReviewRow(null);
            reload();
          }}
        />
      )}

      {logRow && (
        <ModalInheritanceLog
          id={logRow.id_inheritance_transfer}
          inheritanceNo={logRow.inheritance_no}
          onClose={() => setLogRow(null)}
        />
      )}
    </>
  );
}
